import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import toast from 'react-hot-toast';
import { StyledTabs, StyledTab } from '../../../styles/StyledComponents';
import { useEventsListQuery } from '../../../api/use-events-list-query';
import { useUpdateEventPublishedMutation } from '../../../api/use-update-event-published-mutation';
import { useDeleteEventMutation } from '../../../api/use-delete-event-mutation';
import {
  EVENTS_SCOPE,
  canPublishEvent,
  useEventsScope,
} from '../../../contexts/EventsScopeContext';
import EventsListToolbar from './EventsListToolbar';
import EventsListTable from './EventsListTable';
import EventsGridView from './EventsGridView';
import DeleteEventDialog from './DeleteEventDialog';
import PortalPaginationControls from '../../layout/PortalPaginationControls';
import {
  EVENT_TABS,
  EVENT_VIEW_MODES,
  EVENTS_PER_PAGE,
  buildEventsListQueryParams,
  getEventListEventType,
} from '../eventsListData';

const ListPanel = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(2),
}));

const MAX_API_LIMIT = 100;
const SEARCH_DEBOUNCE_MS = 400;

const EventsListSection = () => {
  const navigate = useNavigate();
  const { routes, features, scope } = useEventsScope();
  const [deleteEventId, setDeleteEventId] = useState(null);
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [viewMode, setViewMode] = useState(EVENT_VIEW_MODES.LIST);
  const [page, setPage] = useState(1);
  const [publishedOverrides, setPublishedOverrides] = useState({});
  const [togglingPublishedId, setTogglingPublishedId] = useState(null);

  const updatePublishedMutation = useUpdateEventPublishedMutation();
  const deleteEventMutation = useDeleteEventMutation();

  const eventType = getEventListEventType(tab);
  const isReviewTab = eventType === EVENT_TABS.REVIEW;
  const isPastTab = eventType === EVENT_TABS.PAST;
  const isViewOnlyActionsTab = isReviewTab || isPastTab;

  const limit = Math.min(EVENTS_PER_PAGE, MAX_API_LIMIT);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [search]);

  const queryParams = useMemo(
    () =>
      buildEventsListQueryParams({
        limit,
        page,
        eventType,
        categoryType: typeFilter,
        search: debouncedSearch,
      }),
    [limit, page, eventType, typeFilter, debouncedSearch],
  );

  const { data, isLoading, isFetching, isError, error } = useEventsListQuery(queryParams);

  const events = useMemo(() => {
    const list = data?.events ?? [];
    return list.map((event) => ({
      ...event,
      published:
        publishedOverrides[event.id] !== undefined ? publishedOverrides[event.id] : event.published,
    }));
  }, [data?.events, publishedOverrides]);

  const apiTotal = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(apiTotal / limit));
  const currentPage = Math.min(page, totalPages);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setPage(1);
  };

  const handleTabChange = (_, newValue) => {
    setTab(newValue);
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleTypeFilterChange = (e) => {
    setTypeFilter(e.target.value);
    setPage(1);
  };

  const isPublishDisabled = (event) =>
    isPastTab ||
    isReviewTab ||
    togglingPublishedId === event.id ||
    !canPublishEvent(event, features);

  const handleViewEvent = (id) => {
    if (eventType === EVENT_TABS.REVIEW) {
      navigate(`${routes.detail(id)}?mode=review`);
      return;
    }
    navigate(routes.detail(id));
  };

  const handleEditEvent = (id) => {
    navigate(routes.edit(id));
  };

  const handleDeleteRequest = (id) => {
    setDeleteEventId(id);
  };

  const handleDeleteClose = () => {
    setDeleteEventId(null);
  };

  const handlePublishedChange = (id, published) => {
    if (isPastTab) return;

    const event = events.find((item) => item.id === id);
    if (event && !canPublishEvent(event, features)) {
      toast.error('Only approved events can be published.');
      return;
    }

    const previousPublished = event?.published;

    setPublishedOverrides((prev) => ({ ...prev, [id]: published }));
    setTogglingPublishedId(id);

    updatePublishedMutation.mutate(
      { eventId: id, published },
      {
        onSuccess: () => {
          setPublishedOverrides((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
        },
        onError: (err) => {
          setPublishedOverrides((prev) => {
            const next = { ...prev };
            if (previousPublished !== undefined) {
              next[id] = previousPublished;
            } else {
              delete next[id];
            }
            return next;
          });
          toast.error(err?.message || 'Failed to update published status');
        },
        onSettled: () => {
          setTogglingPublishedId(null);
        },
      },
    );
  };

  const handleDeleteConfirm = () => {
    if (!deleteEventId || deleteEventMutation.isPending) return;

    const idToDelete = deleteEventId;

    deleteEventMutation.mutate(idToDelete, {
      onSuccess: () => {
        setPublishedOverrides((prev) => {
          const next = { ...prev };
          delete next[idToDelete];
          return next;
        });
        setDeleteEventId(null);
        toast.success('Event deleted successfully');
      },
      onError: (err) => {
        toast.error(err?.message || 'Failed to delete event');
      },
    });
  };

  const eventToDelete = events.find((e) => e.id === deleteEventId);

  const showEmpty = !isLoading && !isError && events.length === 0;

  return (
    <ListPanel>
      <StyledTabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <StyledTab label="Upcoming" />
        <StyledTab label="Past" />
        <StyledTab label="Review" />
      </StyledTabs>

      <EventsListToolbar
        search={search}
        onSearchChange={handleSearchChange}
        typeFilter={typeFilter}
        onTypeFilterChange={handleTypeFilterChange}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
      />

      {isLoading ? (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress sx={{ color: 'primary.main' }} />
        </Box>
      ) : isError ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body1" color="error">
            {error?.message || 'Failed to load events.'}
          </Typography>
        </Box>
      ) : showEmpty ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No events found.
          </Typography>
        </Box>
      ) : viewMode === EVENT_VIEW_MODES.GRID ? (
        <EventsGridView
          events={events}
          onEventClick={handleViewEvent}
          onView={handleViewEvent}
          onEdit={isViewOnlyActionsTab ? undefined : handleEditEvent}
          onDelete={isViewOnlyActionsTab ? undefined : handleDeleteRequest}
          onPublishedChange={handlePublishedChange}
          togglingPublishedId={togglingPublishedId}
          isPublishDisabled={isPublishDisabled}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          <EventsListTable
            events={events}
            onView={handleViewEvent}
            onEdit={isViewOnlyActionsTab ? undefined : handleEditEvent}
            onDelete={isViewOnlyActionsTab ? undefined : handleDeleteRequest}
            onPublishedChange={handlePublishedChange}
            isPublishDisabled={isPublishDisabled}
            showApprovalStatus={features.publishRequiresApproved}
            showCreatedBy={scope === EVENTS_SCOPE.ADMIN}
            containerSx={{
              maxHeight: 'calc(100vh - 360px)',
              minHeight: 0,
              overflow: 'auto',
              borderRadius: 0,
              boxShadow: 'none',
            }}
          />
          {!isLoading && !isError && apiTotal > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.5,
                borderTop: '1px solid',
                borderTopColor: 'divider',
                bgcolor: 'background.paper',
                flexShrink: 0,
              }}
            >
              <PortalPaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                isLoading={isFetching}
                onPageChange={setPage}
              />
            </Box>
          )}
        </Box>
      )}

      <DeleteEventDialog
        open={Boolean(deleteEventId)}
        event={eventToDelete}
        deleting={deleteEventMutation.isPending}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </ListPanel>
  );
};

export default EventsListSection;
